import { format } from "date-fns";

import { Theme } from "@/domain/entities/theme.entity";
import { apiClient } from "@/data/api/axios-client";
import { createApiError } from "@/domain/errors/api.error";
import {
  ThemeListResponseDTO,
  AvailabilitySlotDTO,
  AvailabilityResponseDTO,
} from "../dto/theme.dto";

const MOCK_THEMES: Theme[] = [
    {
        id: 'theme-minimalist',
        name: 'Minimalist White',
        description: 'Clean, bright, and timeless. Perfect for individual portraits and professional headshots.',
        imageUrl: 'https://images.unsplash.com/photo-1494438639946-1ebd1d20bf85?q=80&w=800&auto=format&fit=crop',
        price: 0,
        isActive: true,
    },
    {
        id: 'theme-vintage',
        name: 'Vintage Warmth',
        description: 'Cozy tones and nostalgic vibes. Ideal for family gatherings and couples.',
        imageUrl: 'https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=800&auto=format&fit=crop',
        price: 0,
        isActive: true,
    },
    {
        id: 'theme-midnight',
        name: 'Midnight Elegance',
        description: 'Moody and sophisticated. Best for dramatic portraits and high-fashion looks.',
        imageUrl: 'https://images.unsplash.com/photo-1554048612-b6a482bc67e5?q=80&w=800&auto=format&fit=crop',
        price: 0,
        isActive: true,
    },
];

interface GetThemesOptions {
  module?: string;
  useMock?: boolean;
}

/**
 * Repository for theme data.
 * Supports both real API and mock data via parameter.
 */
export const themeRepository = {
    getThemes: async (options: GetThemesOptions = {}): Promise<Theme[]> => {
        const { module, useMock = false } = options;

        if (useMock) {
            await new Promise((resolve) => setTimeout(resolve, 500));
            return MOCK_THEMES;
        }

        const endpoint = process.env.NEXT_PUBLIC_THEMES_ENDPOINT;

        if (!endpoint) {
            throw createApiError({
                statusCode: 500,
                message: "Themes endpoint is not configured.",
                code: "THEMES_ENDPOINT_MISSING",
            });
        }

        const response = await apiClient.get<ThemeListResponseDTO>(
            endpoint,
            module ? { params: { module } } : undefined
        );
        return Array.isArray(response.data) ? response.data : [];
    },

    getAvailableTimes: async (themeId: string, date: Date, packageId?: string, useMock: boolean = false): Promise<AvailabilitySlotDTO[]> => {
        if (useMock) {
            await new Promise((resolve) => setTimeout(resolve, 300));

            const slots: AvailabilitySlotDTO[] = [];
            for (let hour = 10; hour < 18; hour++) {
                for (let min = 0; min < 60; min += 20) {
                    const h = hour > 12 ? hour - 12 : hour;
                    const ampm = hour >= 12 ? 'PM' : 'AM';
                    const time = `${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')} ${ampm}`;
                    slots.push({
                        time,
                        themeId,
                        available: Math.random() > 0.3
                    });
                }
            }
            return slots;
        }

        const dateStr = format(date, 'yyyy-MM-dd');
        const endpoint = (process.env.NEXT_PUBLIC_AVAILABILITY || '/api/themes/{themeId}/available-times').replace('{themeId}', themeId);

        const response = await apiClient.get<AvailabilityResponseDTO>(endpoint, {
            params: {
                date: dateStr,
                packageId
            }
        });

        return response.data.slots;
    },
};
